/* 
 * GEMINI 2.0 CUSTOMIZATION
 * Clone of related-products.js
 * Modified to support dynamic API section targeting via 'data-api-section' attribute.
 * Added support for Gemini mobile dots interaction.
 */
(function() {
  "use strict";

  // Gemini2: Prevent duplicate execution when script is loaded by multiple sections
  if (window._relatedProductsGemini2Loaded) return;
  window._relatedProductsGemini2Loaded = true;
  const SELECTORS = {
    TEMPLATE: "[data-api-related-template]",
    ITEM: ".product-item",
    GRID_ITEM: "[data-grid-item]",
    SECTION: "[data-related-section]",
    RECENTLY_VIEWED: "recently-viewed",
    GRID_SLIDER: "grid-slider",
    WRAPPER: "[data-recently-viewed-wrapper]",
    SLIDER_DOTS: ".product-item__bg__slider__dot",
    HOVER_SLIDER: "[data-hover-slider]"
  };

  if (!customElements.get("related-products-gemini")) {
    customElements.define("related-products-gemini", class extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.id;
        this.relatedItems = 0;
        this.recentlyViewedVisible = false;
        this.wrapper = this.querySelector(SELECTORS.WRAPPER);
        this.recentlyViewed = this.querySelector(SELECTORS.RECENTLY_VIEWED);
      }

      connectedCallback() {
        // ANATE-PERF R9: when data-load-margin is set (px), defer the recommendations fetch
        // until the section scrolls within that distance of the viewport. Without the
        // attribute (or without IO support) behavior is unchanged: fetch immediately.
        const loadMargin = parseInt(this.dataset.loadMargin, 10);
        if (!isNaN(loadMargin) && loadMargin >= 0 && 'IntersectionObserver' in window) {
          const io = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              io.disconnect();
              this.loadRelatedProducts();
            }
          }, { rootMargin: `${loadMargin}px 0px ${loadMargin}px 0px` });
          io.observe(this);
        } else {
          this.loadRelatedProducts();
        }

        // Not gated: this only wires up listeners for the <recently-viewed> element
        // (which gates its own fetch), so it must run before that element loads.
        this.loadRecentlyViewedProducts();
        
        // GEMINI 2.0: Listen for tab changes to re-initialize sliders that were hidden
        this.addEventListener('theme:tab:change', (e) => {
             // Give the browser a moment to paint the new tab content
             requestAnimationFrame(() => {
                 this.initDots(this);
                 
                 // Also force a scroll event on any sliders to ensure dots sync
                 const visibleSliders = this.querySelectorAll(SELECTORS.HOVER_SLIDER);
                 visibleSliders.forEach(slider => {
                     if (slider.clientWidth > 0) {
                         // Dispatch a fake scroll event to update dots immediately
                         slider.dispatchEvent(new Event('scroll'));
                     }
                 });
             });
        });
      }

      loadRelatedProducts() {
        const section = this.querySelector(SELECTORS.SECTION);
        if (!section) return;
        
        const productId = section.getAttribute("data-product-id");
        const limit = section.getAttribute("data-limit");
        const api = section.getAttribute("data-api-section") || "api-product-recommendation";
        const url = `${window.theme.routes.product_recommendations_url}?section_id=${api}&limit=${limit}&product_id=${productId}&intent=related`;

        fetch(url)
          .then(res => res.text())
          .then(text => this.handleRelatedProductsResponse(text, section))
          .catch(() => this.hideSection(section));
      }

      handleRelatedProductsResponse(text, section) {
        const div = document.createElement("div");
        const parser = new DOMParser();
        const html = parser.parseFromString(text, "text/html");
        const template = html.querySelector(SELECTORS.TEMPLATE);
        
        if (template) {
            div.innerHTML = template.innerHTML;
            const items = div.querySelectorAll(SELECTORS.GRID_ITEM);
            
            if (items.length > 0) {
                // GEMINI 2.0 FIX: Append to placeholder instead of replacing entire section (preserves arrows)
                let productWrapper = section.querySelector('.grid-outer');
                const fetchedContent = div.querySelector('.grid-outer');
                
                if (productWrapper && fetchedContent) {
                    // Placeholder exists, inject the inner content
                    productWrapper.innerHTML = fetchedContent.innerHTML;
                } else if (fetchedContent) {
                    // No placeholder, append after arrows
                    section.insertAdjacentHTML('beforeend', fetchedContent.outerHTML);
                    productWrapper = section.querySelector('.grid-outer');
                } else {
                    // Fallback: old behavior
                    section.innerHTML = div.innerHTML;
                }
                
                this.relatedItems = items.length;
                
                // GEMINI 2.0: Removed theme class-adding logic (grid--slider, grid--mobile-slider)
                // These theme classes conflict with our custom Gemini CSS
                
                this.initDots(section);
                
                // GEMINI 2.0: Reset scroll position to start (respects scroll-padding-left)
                const gridOuter = section.querySelector('.grid-outer-gemini');
                if (gridOuter) {
                    gridOuter.scrollLeft = 0;
                }
                
                // GEMINI 2.0: Dispatch event so mobile dots can initialize
                section.dispatchEvent(new CustomEvent('theme:related-products:loaded', { bubbles: true }));
            } else {
                this.hideSection(section);
            }
        }
        
        this.updateVisibility();
      }

      loadRecentlyViewedProducts() {
        if (this.recentlyViewed) {
            // Listen for the event in case it loads later
            this.recentlyViewed.addEventListener("theme:recently-viewed:loaded", () => {
                this.handleRecentlyViewedResponse();
            });

            // GEMINI 2.0: Check if it's ALREADY loaded (race condition fix)
            // If the inner content container has children, it might be ready.
            const content = this.recentlyViewed.querySelector('.grid-outer-gemini');
            if (content && content.children.length > 0) {
                 this.handleRecentlyViewedResponse();
            }
        }
      }

      handleRecentlyViewedResponse() {
        const minimum = parseInt(this.recentlyViewed.dataset.minimum) || 1;
        const items = this.recentlyViewed.querySelectorAll(SELECTORS.ITEM);
        const slider = this.recentlyViewed.querySelector(SELECTORS.GRID_SLIDER);
        
        const hasItemsWithoutWrapper = !this.wrapper && items.length > 0;
        const hasEnoughItemsWithWrapper = this.wrapper && items.length >= minimum;

        if (hasItemsWithoutWrapper || hasEnoughItemsWithWrapper) {
            if (hasEnoughItemsWithWrapper) {
                this.wrapper.classList.remove("hidden");
            }
            this.recentlyViewedVisible = true;
            this.recentlyViewed.classList.remove("hidden");
            this.recentlyViewed.dispatchEvent(new CustomEvent("theme:tab:check", { bubbles: true }));
            
            if (slider) {
                slider.dispatchEvent(new CustomEvent("theme:grid-slider:init", { bubbles: true }));
            }
            
            this.initDots(this.recentlyViewed);
        }
        
        this.updateVisibility();
      }

      hideSection(element) {
        element.dispatchEvent(new CustomEvent("theme:tab:hide", { bubbles: true }));
      }

      updateVisibility() {
        const hide = this.relatedItems < 1 && !this.recentlyViewedVisible;
        this.classList.toggle("hidden", hide);
      }
      
      initDots(container) {
         // GEMINI 2.0: Use ResizeObserver to handle initialization when elements become visible
         // This fixes the issue where sliders inside hidden tabs (like 'Recently Viewed') have 0 width
         // and fail to initialize until the tab is clicked.
         
         const sliders = container.querySelectorAll(SELECTORS.HOVER_SLIDER);
         
         if (sliders.length === 0) return;

         const observer = new ResizeObserver((entries) => {
             for (const entry of entries) {
                 const slider = entry.target;
                 
                 // Only initialize if the element is visible (has width)
                 if (entry.contentRect.width > 0) {
                     // Check if already initialized to avoid duplicate listeners
                     if (slider.dataset.dotsInit === "true") continue;
                     
                     // Find dots container relative to this item
                     const dotsContainer = slider.parentElement.querySelector('.product-item__bg__slider__dots');
                     if (!dotsContainer) continue;
                     
                     const dots = dotsContainer.querySelectorAll(SELECTORS.SLIDER_DOTS);
                     if (dots.length > 0) {
                         // Mark as initialized
                         slider.dataset.dotsInit = "true";

                         // Attach Scroll Listener once per slider with rAF coalescing
                         const updateDotsSelection = () => {
                             const scrollLeft = slider.scrollLeft;
                             const width = slider.clientWidth;
                             if (width === 0) return; // Guard against rapid hiding

                             const index = Math.round(scrollLeft / width);

                             dots.forEach((dot, i) => {
                                 if (i === index) {
                                     dot.classList.add('is-selected');
                                 } else {
                                     dot.classList.remove('is-selected');
                                 }
                             });
                         };
                         slider._geminiDotsScrollHandler = slider._geminiDotsScrollHandler || (() => {
                           if (slider._geminiDotsRaf) return;
                           slider._geminiDotsRaf = window.requestAnimationFrame(() => {
                             slider._geminiDotsRaf = null;
                             updateDotsSelection();
                           });
                         });
                         slider.addEventListener('scroll', slider._geminiDotsScrollHandler, { passive: true });

                         // Initial Sync (in case it starts at index 0 but logic didn't run)
                         updateDotsSelection();
                     }
                     
                     // Optional: Stop observing this specific element once initialized? 
                     // No, because if screen resizes drastically we might need to re-calc, 
                     // though CSS scroll-snap handles most of it. 
                     // We keep observing to support potential future "un-hide" scenarios if needed,
                     // but the flag prevents re-binding.
                 }
             }
         });

         sliders.forEach(slider => observer.observe(slider));
      }
    });
  }

  // GEMINI 2.0: Mobile Slider Dots Custom Element
  if (!customElements.get('mobile-slider-dots')) {
       class MobileSliderDots extends HTMLElement {
           constructor() {
               super();
               this.dotsContainer = this;
               this.container = null;
               this.items = [];
               this.updateDots = this.updateDots.bind(this);
               this.scheduleUpdate = this.scheduleUpdate.bind(this);
               this.handleScroll = this.handleScroll.bind(this);
               this.handleResize = this.handleResize.bind(this);
               this.handleDotClick = this.handleDotClick.bind(this);
               this.init = this.init.bind(this);
               this.updateRaf = null;
               this.bound = false;
           }

           connectedCallback() {
               // Initial check
               this.init();
               
               // GEMINI 2.0: Listen for AJAX content loaded event
               this.handleAjaxLoaded = () => {
                   setTimeout(() => this.init(), 50); // Small delay for DOM settle
               };
               document.addEventListener('theme:related-products:loaded', this.handleAjaxLoaded);
               
               // MutationObserver as backup for content injection
               const parent = this.closest('[data-gemini-slider-container]') || 
                              this.parentElement?.previousElementSibling;
               if (parent) {
                   this.observer = new MutationObserver(() => {
                       if (!this.container) this.init();
                   });
                   this.observer.observe(parent, { childList: true, subtree: true });
               }
           }
           
           init() {
               // GEMINI 2.0: Robust container finding logic
               this.container = null;

               // 1. Try finding .grid-outer-gemini in previous sibling (Structure: Container + Dots)
               // Check parent's previous sibling (if dots are wrapped) or direct previous sibling
               const prevElement = this.dotsContainer.parentElement?.previousElementSibling || this.dotsContainer.previousElementSibling;
               
               if (prevElement) {
                   if (prevElement.classList.contains('grid-outer-gemini')) {
                       this.container = prevElement;
                   } else if (prevElement.querySelector) {
                       this.container = prevElement.querySelector('.grid-outer-gemini');
                   }
               }

               // 2. Fallback: Try closest grid-outer wrapper (Legacy/Snippet structure)
               if (!this.container) {
                   const wrapper = this.closest('.grid-outer');
                   if (wrapper) {
                       this.container = wrapper.querySelector('.grid-outer-gemini');
                   }
               }
               
               // 3. Fallback: Try parent container (Direct child of section)
               if (!this.container) {
                   const parent = this.closest('.collection__products') || this.closest('[data-gemini-slider-container]');
                   if (parent) {
                       this.container = parent.querySelector('.grid-outer-gemini');
                   }
               }

               if (!this.container) return;
               
               // Disconnect observer if found
               if (this.observer) this.observer.disconnect();

               this.updateDots();
               if (!this.bound) {
                 this.container.addEventListener('scroll', this.handleScroll, { passive: true });
                 window.addEventListener('resize', this.handleResize, { passive: true });
                 this.dotsContainer.addEventListener('click', this.handleDotClick);
                 this.bound = true;
               }
           }

           disconnectedCallback() {
               if (this.container) {
                   this.container.removeEventListener('scroll', this.handleScroll);
               }
               window.removeEventListener('resize', this.handleResize);
               this.dotsContainer.removeEventListener('click', this.handleDotClick);
               document.removeEventListener('theme:related-products:loaded', this.handleAjaxLoaded);
               if (this.updateRaf) {
                 window.cancelAnimationFrame(this.updateRaf);
                 this.updateRaf = null;
               }
               if (this.observer) this.observer.disconnect();
               this.bound = false;
           }

           handleScroll() {
               this.scheduleUpdate();
           }

           handleResize() {
               this.scheduleUpdate();
           }

           scheduleUpdate() {
               if (this.updateRaf) return;
               this.updateRaf = window.requestAnimationFrame(() => {
                 this.updateRaf = null;
                 this.updateDots();
               });
           }

           handleDotClick(event) {
               const dot = event.target.closest('.mobile-slider-dot');
               if (!dot || !this.container || !this.items.length) return;
               const index = Number(dot.dataset.index);
               if (!Number.isFinite(index)) return;
               const targetItem = this.items[index];
               if (!targetItem) return;
               this.container.scrollTo({
                 left: targetItem.offsetLeft - this.container.offsetLeft,
                 behavior: 'smooth'
               });
           }

           updateDots() {
               if (window.innerWidth > 749) return; 
               if (!this.container) return;

               this.items = Array.from(this.container.querySelectorAll('.grid-item-gemini'));
               if (this.items.length === 0) return;

               // READ PHASE: measure scroll position and item offsets BEFORE any DOM
               // write, so reading offsetLeft never forces a synchronous reflow.
               const scrollLeft = this.container.scrollLeft;
               let activeIndex = 0;
               let minDiff = Infinity;

               this.items.forEach((item, idx) => {
                    const itemLeft = item.offsetLeft;
                    const diff = Math.abs(itemLeft - scrollLeft);

                    if (diff < minDiff) {
                        minDiff = diff;
                        activeIndex = idx;
                    }
               });

               // WRITE PHASE: (re)build dots only when the count changed.
               if (this.dotsContainer.children.length !== this.items.length) {
                    this.dotsContainer.innerHTML = '';
                    this.items.forEach((_, idx) => {
                        const dot = document.createElement('button');
                        dot.classList.add('mobile-slider-dot');
                        dot.setAttribute('data-index', String(idx));
                        dot.setAttribute('aria-label', `Go to slide ${idx + 1}`);
                        if (idx === 0) dot.setAttribute('aria-current', 'true');
                        this.dotsContainer.appendChild(dot);
                    });
               }

               Array.from(this.dotsContainer.children).forEach((dot, idx) => {
                   if (idx === activeIndex) {
                       dot.setAttribute('aria-current', 'true');
                   } else {
                       dot.removeAttribute('aria-current');
                   }
               });
           }
       }
       customElements.define('mobile-slider-dots', MobileSliderDots);
   }
})();